import { useCurrentAgent } from '@/features/chats/stores';
import { Agent } from '@/entities/agent';

export interface AgentModelAvailability {
  /** Whether the agent declares that it requires model selection */
  requiresModels: boolean;
  /** Whether the agent has any enabled models available */
  hasEnabledModels: boolean;
  /** Whether the agent can be used (either doesn't need models, or has them) */
  canUseAgent: boolean;
}

/**
 * Returns information about the current agent's model requirements and availability.
 *
 * Use this to determine if the agent can be used based on model whitelists
 */
export const useAgentModelAvailability = (): AgentModelAvailability => {
  const agent: Agent | null = useCurrentAgent();

  // If no agent selected, cannot use
  if (!agent) {
    return {
      requiresModels: false,
      hasEnabledModels: false,
      canUseAgent: false,
    };
  }

  // Check if agent declares that it supports/requires models
  const requiresModels = !!(
    agent.capabilities?.supportedModels && agent.capabilities.supportedModels.length > 0
  );

  // Check if agent has any enabled models
  const hasEnabledModels = !!(agent.enabledModels && agent.enabledModels.length > 0);

  // Agent can be used if it doesn't require models, OR if it requires them and has them
  const canUseAgent = !requiresModels || hasEnabledModels;

  return {
    requiresModels,
    hasEnabledModels,
    canUseAgent,
  };
};
